import { useMemo, useState } from 'react';
import { Shuffle, Trophy } from 'lucide-react';
import { ReunionAgency, ReunionTransaction } from '../../../hooks/useReunionMensuelleData';
import { PeriodBucket, getPeriodKeyForDate, buildPeriodMultiCategoryCounts } from '../../../utils/reunionMensuelle';
import { Scope, ScopeColumn, getColumnForTransaction } from './ScopeSelector';
import TrendChartCard from './TrendChartCard';
import InfoTooltip from './InfoTooltip';
import SalesListModal from './SalesListModal';

interface InterAgenceTabProps {
  transactions: ReunionTransaction[];
  agencies: ReunionAgency[];
  periods: PeriodBucket[];
  columns: ScopeColumn[];
  scope: Scope;
}

export default function InterAgenceTab({ transactions, agencies, periods, columns, scope }: InterAgenceTabProps) {
  const [modalAgency, setModalAgency] = useState<string | null>(null);

  const agencyToCompany = useMemo(
    () => new Map(agencies.map(a => [a.name, a.company])),
    [agencies]
  );

  const isInterAgenceInterne = (tx: ReunionTransaction): boolean => {
    if (!tx.listing_agency || !tx.buyer_agency) return false;
    if (tx.listing_agency === tx.buyer_agency) return false;
    const listingCompany = agencyToCompany.get(tx.listing_agency);
    const buyerCompany = agencyToCompany.get(tx.buyer_agency);
    return !!listingCompany && listingCompany === buyerCompany;
  };

  const inScope = transactions.filter(tx => getColumnForTransaction(tx, scope) !== null);
  const interAgenceTx = inScope.filter(isInterAgenceInterne);
  const getPeriodKey = (tx: ReunionTransaction) => getPeriodKeyForDate(tx.compromise_date, periods);

  const categories = columns.map(c => ({
    key: c.key,
    label: c.label,
    predicate: (tx: ReunionTransaction) => isInterAgenceInterne(tx) && getColumnForTransaction(tx, scope) === c.key,
  }));

  const counts = buildPeriodMultiCategoryCounts(inScope, periods, categories, getPeriodKey);

  const formatCount = (v: number) => v.toString();

  const ranking = useMemo(() => {
    const stats = new Map<string, { asListing: number; asBuyer: number }>();
    interAgenceTx.forEach(tx => {
      const l = tx.listing_agency!;
      const b = tx.buyer_agency!;
      stats.set(l, { asListing: (stats.get(l)?.asListing ?? 0) + 1, asBuyer: stats.get(l)?.asBuyer ?? 0 });
      stats.set(b, { asListing: stats.get(b)?.asListing ?? 0, asBuyer: (stats.get(b)?.asBuyer ?? 0) + 1 });
    });
    return [...stats.entries()]
      .map(([agency, s]) => ({ agency, ...s, total: s.asListing + s.asBuyer }))
      .sort((a, b) => b.total - a.total);
  }, [interAgenceTx]);

  const maxTotal = ranking[0]?.total ?? 0;

  const agencyColumns = [
    { label: 'Agence vendeur', render: (tx: ReunionTransaction) => tx.listing_agency || '—' },
    { label: 'Agence apporteur', render: (tx: ReunionTransaction) => tx.buyer_agency || '—' },
  ];

  return (
    <div className="space-y-5">
      <TrendChartCard
        icon={<Shuffle size={16} className="text-cyan-600" />}
        iconBg="bg-cyan-50"
        title="Dossiers en inter-agence interne"
        periods={periods}
        columns={columns}
        data={counts}
        valueFormatter={formatCount}
        getTransactions={(periodKey, columnKey) =>
          inScope.filter(tx => getPeriodKey(tx) === periodKey && isInterAgenceInterne(tx) && getColumnForTransaction(tx, scope) === columnKey)
        }
        infoText="Chaque vente est rangée dans le mois/trimestre de sa date de compromis. On compte les dossiers où l'agence du négociateur vendeur diffère de l'agence du négociateur apporteur (acquéreur), les deux agences appartenant à la même société (ex : Rennes Centre vend un bien à un acquéreur apporté par Saint-Grégoire)."
        extraColumns={agencyColumns}
      />

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="w-9 h-9 rounded-xl bg-cyan-50 flex items-center justify-center shadow-sm">
            <Trophy size={16} className="text-cyan-600" />
          </div>
          <span className="text-sm font-semibold text-slate-700">Classement des agences en inter-agence interne</span>
          <InfoTooltip text="Toutes périodes confondues (selon la sélection en cours). Vendeur = l'agence a vendu un bien apporté par une autre agence. Apporteur = l'agence a apporté l'acquéreur d'un bien d'une autre agence. Cliquer sur une ligne affiche les dossiers concernés." />
        </div>

        {ranking.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-400 italic">Aucun dossier en inter-agence interne sur la période sélectionnée</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className="px-4 py-2 text-left font-semibold text-slate-500 text-xs uppercase tracking-wide">Agence</th>
                  <th className="px-4 py-2 text-right font-semibold text-slate-500 text-xs uppercase tracking-wide">Vendeur</th>
                  <th className="px-4 py-2 text-right font-semibold text-slate-500 text-xs uppercase tracking-wide">Apporteur</th>
                  <th className="px-4 py-2 text-right font-semibold text-slate-500 text-xs uppercase tracking-wide">Total</th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-500 text-xs uppercase tracking-wide w-1/3"></th>
                </tr>
              </thead>
              <tbody>
                {ranking.map(row => (
                  <tr
                    key={row.agency}
                    onClick={() => setModalAgency(row.agency)}
                    className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-2 text-slate-700 font-medium">{row.agency}</td>
                    <td className="px-4 py-2 text-right text-slate-600">{row.asListing}</td>
                    <td className="px-4 py-2 text-right text-slate-600">{row.asBuyer}</td>
                    <td className="px-4 py-2 text-right font-bold text-indigo-600">{row.total}</td>
                    <td className="px-4 py-2">
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full bg-cyan-500 rounded-full"
                          style={{ width: maxTotal > 0 ? `${(row.total / maxTotal) * 100}%` : '0%' }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalAgency && (
        <SalesListModal
          title={`Inter-agence interne — ${modalAgency}`}
          transactions={interAgenceTx.filter(tx => tx.listing_agency === modalAgency || tx.buyer_agency === modalAgency)}
          onClose={() => setModalAgency(null)}
          extraColumns={agencyColumns}
        />
      )}
    </div>
  );
}
